import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Building2, Mail, MapPin, Scale, ShieldCheck, BookOpen, ExternalLink, Server, FileLock2, AlertTriangle, Newspaper, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MobileCollapse } from '../components/MobileCollapse';
import { PageBreadcrumb } from '../components/PageBreadcrumb';

/**
 * Page Mentions légales — conforme art. 6 LCEN + positionnement non lucratif.
 * Sections 2-7 repliables sur mobile (MobileCollapse), visibles sur desktop.
 * Section 1 (Mission) garde un rendu pleine lisibilité (pivot juridique).
 */
export default function MentionsLegales() {
  useEffect(() => {
    document.title = 'Mentions légales — CODE 2 ROUES';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        "Mentions légales de Code 2 Roues : plateforme éditoriale et pédagogique indépendante éditée par Bruno LLORENS (ECSR). Pas une auto-école — aucune affiliation Codes Rousseau, ENPC, Ediser ou autres éditeurs commerciaux du Code de la route."
      );
    }
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-4 md:space-y-6" data-testid="mentions-legales-page">
      <PageBreadcrumb items={[{ label: 'Mentions légales' }]} />
      <div className="flex items-center gap-2">
        <Link to="/" data-testid="mentions-back">
          <Button variant="ghost" size="sm" className="-ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'accueil
          </Button>
        </Link>
      </div>

      {/* Hero */}
      <section className="mc-card relative overflow-hidden p-5 sm:p-8" data-testid="mentions-hero">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1 inline-flex items-center gap-1">
            <Scale className="w-3 h-3" /> Mentions légales
          </div>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight">Mentions légales &amp; positionnement</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Conformément à l'article 6 de la LCEN (n°2004-575) et au RGPD.
          </p>
          <p className="text-[11px] text-muted-foreground mt-2">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Renvoi croisé vers la charte éditoriale — bloc bien visible */}
      <Link
        to="/charte-editoriale"
        className="group block rounded-2xl border-2 border-[hsl(var(--primary))]/25 bg-gradient-to-r from-[hsl(var(--primary))]/[0.06] via-[hsl(var(--primary))]/[0.03] to-transparent hover:border-[hsl(var(--primary))]/60 hover:from-[hsl(var(--primary))]/[0.10] transition-all p-5 sm:p-6"
        data-testid="mentions-charte-crosslink"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
            <Newspaper className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-1">
              À lire aussi · Complément essentiel
            </div>
            <h2 className="text-base sm:text-lg font-black leading-tight mb-1.5">
              Notre charte éditoriale
            </h2>
            <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">
              Découvrez notre <strong>positionnement média indépendant</strong>, nos
              <strong> 6 engagements</strong>, nos <strong>sources officielles</strong> (ONISR, REMC,
              Code de la route) et notre <strong>respect explicite des marques tierces</strong>
              (Codes Rousseau®, ENPC®, Ediser®, Prevent®).
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0 self-center group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      {/* 1. Mission non lucrative — toujours visible (pivot juridique) */}
      <section className="mc-card p-5 sm:p-6 border-l-4" style={{ borderLeftColor: '#16a34a' }} data-testid="mentions-mission">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-[#16a34a]/10 text-[#16a34a]">
            <BadgeCheck className="w-5 h-5" />
          </div>
          <h2 className="text-lg sm:text-xl font-black tracking-tight">1. Mission &amp; positionnement</h2>
        </div>
        <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
          <p>
            <strong>Code 2 Roues est une plateforme éditoriale et pédagogique indépendante</strong> dédiée
            à la sécurité routière 2 roues et à la connaissance du Code de la route. Elle a été conçue
            dans une démarche de <strong>transfert de savoir à but non lucratif</strong> par <strong>Bruno LLORENS</strong>,
            enseignant de la conduite et de la sécurité routière (ECSR) diplômé d'État, exerçant à Bourgoin-Jallieu (Nord Isère).
          </p>
          <p>
            <strong>Code 2 Roues N'EST PAS une auto-école ni une moto-école</strong> : aucune activité
            commerciale d'enseignement de la conduite n'y est exercée, aucun agrément préfectoral
            d'enseignement n'y est attaché. La plateforme ne forme pas d'élèves, ne délivre pas de livret
            d'apprentissage et ne présente aucun candidat à l'examen.
          </p>
          <p>
            <strong>Code 2 Roues n'organise pas, ne vend pas et ne réserve aucun passage de l'examen officiel.</strong>
            L'examen théorique s'effectue exclusivement auprès des opérateurs privés agréés par l'État :
          </p>
          <ul className="list-none pl-0 space-y-1.5">
            {[
              { name: 'La Poste', url: 'https://www.lecode.laposte.fr/' },
              { name: 'SGS', url: 'https://www.objectifcode.sgs.com/' },
              { name: 'Dekra (ExaCode)', url: 'https://www.dekra-exacode.fr/' },
              { name: 'Pearson VUE (Code\u2019nGo!)', url: 'https://www.codengo.fr/' },
              { name: 'Bureau Veritas', url: 'https://www.bureauveritas-codedelaroute.fr/' },
            ].map((op) => (
              <li key={op.name} className="flex items-center gap-2 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a] flex-shrink-0" />
                <a href={op.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]">
                  {op.name} <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              </li>
            ))}
          </ul>
          <p className="italic text-xs text-muted-foreground">
            <strong>Aucune affiliation, partenariat ni accord</strong> avec les éditeurs commerciaux du Code
            de la route (Codes Rousseau®, ENPC®, Ediser®, Prevent®, Easy Systems®, et autres). Les marques
            citées restent la propriété de leurs détenteurs respectifs. Voir notre{' '}
            <Link to="/charte-editoriale" className="underline font-bold hover:text-[hsl(var(--primary))]" data-testid="mentions-mission-charte-link">
              charte éditoriale
            </Link>{' '}
            pour le détail de notre positionnement média indépendant.
          </p>
          <p>
            L'utilisation de Code 2 Roues est <strong>entièrement gratuite</strong> pour les candidats au permis
            (AM/BSR, A1, A2, passerelle A2→A, 125 B→A1, L5e). Aucun frais caché.
          </p>
          <p className="text-xs text-muted-foreground italic mt-3 pt-3 border-t border-foreground/8">
            <strong>Note :</strong> Bruno LLORENS exerce parallèlement, à titre individuel, une activité
            de <strong>moniteur indépendant en micro-entreprise</strong> à destination des auto-écoles et
            moto-écoles partenaires. Cette activité commerciale est <strong>distincte</strong> de l'édition
            de la plateforme code2roues.fr (laquelle demeure gratuite et éditoriale) et n'est accessible
            qu'aux établissements professionnels via contact direct.
          </p>
        </div>
      </section>

      {/* Sections repliables sur mobile, visibles desktop */}
      <div className="mc-card p-4 sm:p-6">
        <MobileCollapse title="2. Éditeur du site" icon={Building2} level="h2" testId="mentions-editor">
          <dl className="grid sm:grid-cols-[180px_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="font-bold text-foreground/70">Éditeur</dt>
            <dd>Bruno LLORENS, Enseignant de la Conduite et de la Sécurité Routière (ECSR) diplômé d'État</dd>
            <dt className="font-bold text-foreground/70">Statut juridique</dt>
            <dd>
              <strong>Moniteur indépendant en micro-entreprise</strong> — TVA non applicable (art. 293 B du CGI).{' '}
              <span className="text-muted-foreground italic">Pas d'auto-école, pas d'agrément préfectoral d'enseignement de la conduite. Activité de prestation pour établissements agréés uniquement.</span>
            </dd>
            <dt className="font-bold text-foreground/70">Site web</dt>
            <dd>
              Plateforme <strong>éditoriale et pédagogique non lucrative</strong> (LCEN art. 6, III, 2°) — gratuite, sans publicité, sans revente de données.
            </dd>
            <dt className="font-bold text-foreground/70">Marque éditoriale</dt>
            <dd>« LLB ECSR » — pour <strong>L</strong>LORENS <strong>B</strong>runo, <strong>E</strong>nseignant de la <strong>C</strong>onduite et de la <strong>S</strong>écurité <strong>R</strong>outière</dd>
            <dt className="font-bold text-foreground/70">Qualifications</dt>
            <dd>TP ECSR · CCS 2 Roues · Autorisation d'enseigner en cours de validité</dd>
            <dt className="font-bold text-foreground/70">Adresse pro</dt>
            <dd className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />Bourgoin-Jallieu, Nord Isère (38), France</dd>
            <dt className="font-bold text-foreground/70">Responsable publication</dt>
            <dd>Bruno LLORENS</dd>
            <dt className="font-bold text-foreground/70">Contact</dt>
            <dd className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /><a href="mailto:contact@code2roues.fr" className="hover:text-[hsl(var(--primary))]">contact@code2roues.fr</a></dd>
          </dl>
          <p className="text-xs text-muted-foreground italic mt-4 leading-relaxed">
            <strong>Code 2 Roues n'est pas une auto-école.</strong> Il s'agit d'une plateforme éditoriale indépendante,
            sans agrément préfectoral d'enseignement de la conduite. Aucune affiliation avec les éditeurs
            Codes Rousseau®, ENPC®, Ediser® ou autres marques d'édition du code de la route.
          </p>
        </MobileCollapse>

        <MobileCollapse title="3. Hébergement" icon={Server} level="h2" testId="mentions-hosting">
          <p className="text-sm leading-relaxed text-foreground/85">
            Le site Code 2 Roues est hébergé sur l'infrastructure cloud opérée par
            <strong> Emergent / Kubernetes</strong>. Le nom de domaine
            <strong> code2roues.fr </strong> est enregistré auprès de <strong>OVH SAS</strong>
            (2 rue Kellermann, 59100 Roubaix — France · <a href="https://www.ovhcloud.com/fr/" target="_blank" rel="noopener noreferrer" className="hover:text-[hsl(var(--primary))]">ovhcloud.com</a>).
          </p>
        </MobileCollapse>

        <MobileCollapse title="4. Contenu pédagogique & sources" icon={BookOpen} level="h2" testId="mentions-pedagogy">
          <div className="space-y-2 text-sm leading-relaxed text-foreground/85">
            <p>
              L'ensemble des questions, fiches, enquêtes et schémas pédagogiques publiés sur Code 2 Roues
              est <strong>rédigé de manière originale</strong> par Bruno LLORENS (ECSR), à partir
              <strong> exclusivement de sources officielles publiquement accessibles</strong> :
            </p>
            <ul className="list-none pl-0 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span>Le <strong>Code de la route</strong> en vigueur (Légifrance).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span>Le programme officiel public de l'<strong>Épreuve Théorique Moto</strong> — arrêté du 29 novembre 2019 modifié.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span>Le <strong>REMC</strong> (Référentiel pour l'Éducation à une Mobilité Citoyenne) — arrêté du 23 avril 2012.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span>Les <strong>arrêtés ministériels</strong> régissant les formations Passerelle A2→A (01/06/2016), 7h B→125 (07/01/2019), AM/BSR (08/11/2012).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span>Les <strong>données ONISR</strong> (Observatoire National Interministériel de la Sécurité Routière) et études IFSTTAR / Cerema publiquement consultables.</span>
              </li>
            </ul>
            <p className="italic text-xs text-muted-foreground mt-3 leading-relaxed">
              <strong>Aucun contenu n'est repris, copié ou adapté</strong> des banques de questions ou
              manuels édités par les acteurs commerciaux du Code de la route (Codes Rousseau®, ENPC®,
              Ediser®, Prevent®, Easy Systems®, etc.). Code 2 Roues n'a aucun accord, licence ou affiliation
              avec ces éditeurs.
            </p>
          </div>
        </MobileCollapse>

        <MobileCollapse title="5. Propriété intellectuelle" icon={FileLock2} level="h2" testId="mentions-ip">
          <div className="space-y-2 text-sm leading-relaxed text-foreground/85">
            <p>
              L'ensemble des contenus présents sur Code 2 Roues (textes, questions, illustrations, schémas, code source,
              charte graphique) est protégé par les dispositions du Code de la propriété intellectuelle et reste la
              propriété exclusive de Bruno LLORENS (LLB ECSR).
            </p>
            <p>
              Toute reproduction, représentation, modification, publication ou adaptation, totale ou partielle, est
              strictement interdite sans autorisation écrite préalable, à l'exception des usages strictement personnels
              des candidats utilisant la plateforme à des fins de révision.
            </p>
          </div>
        </MobileCollapse>

        <MobileCollapse title="6. Limitation de responsabilité" icon={AlertTriangle} level="h2" testId="mentions-liability">
          <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
            <p>
              Code 2 Roues est un <strong>outil pédagogique d'entraînement et de préparation</strong>. La réussite
              à l'examen officiel de l'ETM et l'obtention du permis dépendent du candidat, de sa formation pratique
              en école de conduite agréée, et des conditions du jour de l'examen passé auprès d'un opérateur agréé
              par l'État.
            </p>
            <p>
              Bruno LLORENS (LLB ECSR) ne saurait être tenu responsable d'un échec à l'examen officiel,
              ni d'une utilisation non conforme de la plateforme.
            </p>
          </div>
        </MobileCollapse>

        <MobileCollapse title="6bis. Fiabilité du contenu, IA & évolution de la législation" icon={AlertTriangle} level="h2" testId="mentions-content-disclaimer">
          <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 sm:p-4">
              <p className="font-bold text-amber-600 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Avertissement essentiel
              </p>
              <p className="text-xs sm:text-sm">
                Les questions, réponses, explications et contenus pédagogiques diffusés sur Code 2 Roues sont
                rédigés à des <strong>fins de préparation à l'examen uniquement</strong>. Ils ne constituent
                <strong> ni un avis juridique, ni une interprétation officielle</strong> des textes de loi.
                En cas de doute, de question précise ou de litige, seul fait foi le <strong>Code de la route
                publié sur Légifrance</strong> et les éventuelles décisions de justice qui s'y rattachent.
              </p>
            </div>

            <p className="font-bold text-foreground">🛡️ Méthode d'élaboration des contenus</p>
            <p>
              Les contenus sont <strong>élaborés, relus et validés par des Enseignants de la Conduite et de la
              Sécurité Routière (ECSR)</strong> diplômés d'État. Pour accélérer l'enrichissement pédagogique
              (rédaction d'explications, pièges, moyens mnémotechniques, fiches thématiques), Code 2 Roues
              utilise également <strong>des outils d'intelligence artificielle</strong> (modèles de langage),
              dont les productions font systématiquement l'objet d'un <strong>contrôle humain par un ECSR</strong>
              avant publication. Cette démarche est explicitée ici par souci de transparence.
            </p>

            <p className="font-bold text-foreground">⚖️ Évolution permanente du Code de la route</p>
            <p>
              Le Code de la route, les arrêtés, décrets et normes européennes applicables aux deux-roues
              motorisés <strong>évoluent régulièrement</strong> (ex. décret n° 2025-33 du 9 janvier 2025
              sur la circulation inter-files, suppression de la carte verte au 1<sup>er</sup> avril 2024,
              norme ECE 22.06, ZFE, etc.). Malgré les efforts quotidiens de mise à jour de la plateforme,
              <strong> un décalage temporaire peut exister</strong> entre le contenu publié et la législation
              en vigueur le jour de votre consultation ou de votre examen.
            </p>

            <p className="font-bold text-foreground">❌ Exclusion de responsabilité pour erreurs ou désuétudes</p>
            <p>
              Bruno LLORENS (LLB ECSR) et ses collaborateurs s'efforcent d'assurer <strong>l'exactitude,
              la complétude et l'actualité</strong> des informations publiées. Toutefois, malgré ces efforts,
              <strong> aucune garantie</strong> n'est donnée à ce titre. La responsabilité de l'éditeur
              <strong> ne saurait être engagée</strong> au titre :
            </p>
            <ul className="list-none pl-0 space-y-1.5 ml-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                <span>d'une <strong>erreur factuelle, typographique ou d'interprétation</strong> présente
                dans une question, une réponse ou une explication ;</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                <span>d'un <strong>contenu devenu obsolète</strong> suite à une évolution législative,
                réglementaire ou normative postérieure à sa publication ;</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                <span>d'une <strong>divergence entre nos contenus et le sujet officiel</strong> présenté le
                jour de l'Épreuve Théorique Moto (ETM) par l'un des opérateurs agréés par l'État ;</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                <span>d'une <strong>décision personnelle</strong> (administrative, juridique, comportement
                routier) que l'utilisateur prendrait sur la seule base des contenus de Code 2 Roues
                <strong> sans vérification</strong> sur les sources officielles.</span>
              </li>
            </ul>

            <p className="font-bold text-foreground">✅ Signalement d'une erreur ou d'une évolution</p>
            <p>
              Toute erreur, imprécision ou évolution législative peut être <strong>signalée à l'éditeur</strong>
              à l'adresse{' '}
              <a href="mailto:contact@code2roues.fr?subject=Signalement%20de%20contenu" className="underline font-bold text-[hsl(var(--primary))] hover:text-[hsl(var(--primary-dark))]">
                contact@code2roues.fr
              </a>. Les signalements sont examinés sous 72 heures ouvrées. L'éditeur se réserve le droit
              de <strong>corriger, retirer ou mettre à jour</strong> un contenu à tout moment, sans préavis
              ni indemnité.
            </p>

            <p className="font-bold text-foreground">📖 Sources officielles à consulter</p>
            <ul className="list-none pl-0 space-y-1.5 ml-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span><a href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[hsl(var(--primary))]">Code de la route sur Légifrance</a> — source de référence pour les textes en vigueur</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span><a href="https://www.securite-routiere.gouv.fr/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[hsl(var(--primary))]">Sécurité routière (gouv.fr)</a> — campagnes et recommandations officielles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 flex-shrink-0" />
                <span><strong>Votre moto-école agréée</strong> — interlocuteur pédagogique de référence pour votre formation</span>
              </li>
            </ul>

            <p className="text-xs text-muted-foreground italic">
              En utilisant Code 2 Roues, vous reconnaissez avoir pris connaissance de ces limitations et
              acceptez de vous référer aux sources officielles en cas de doute ou d'évolution législative.
            </p>
          </div>
        </MobileCollapse>

        <MobileCollapse title="7. Données personnelles" icon={ShieldCheck} level="h2" testId="mentions-data">
          <p className="text-sm leading-relaxed text-foreground/85">
            Le traitement des données personnelles fait l'objet d'une <Link to="/confidentialite" className="underline font-bold text-[hsl(var(--primary))] hover:text-[hsl(var(--primary-dark))]">politique de confidentialité dédiée</Link>,
            conforme au RGPD (règlement UE 2016/679) et à la loi Informatique et Libertés.
          </p>
        </MobileCollapse>
      </div>

      <div className="text-center text-xs text-muted-foreground py-4">
        © {new Date().getFullYear()} Bruno LLORENS (LLB ECSR) — Code 2 Roues. Tous droits réservés.
      </div>
    </div>
  );
}
